import { z } from 'zod';

/**
 * 用户可见界面支持的语言（官网 / 控制台 / 文档 / API 错误信息 / 邮件）。管理后台只用简体中文。
 * zh-TW、vi、ko 为机器翻译初稿，待母语者校对。
 */
export const LOCALES = ['zh-CN', 'zh-TW', 'en', 'vi', 'ko'] as const;
export type Locale = (typeof LOCALES)[number];
export const LocaleSchema = z.enum(LOCALES);

/** 语言名用各自语言书写（语言切换器里给母语用户看） */
export const LOCALE_LABELS: Record<Locale, string> = {
  'zh-CN': '简体中文',
  'zh-TW': '繁體中文',
  en: 'English',
  vi: 'Tiếng Việt',
  ko: '한국어',
};

/** BCP 47 标签（<html lang>、Intl 格式化） */
export const LOCALE_TAGS: Record<Locale, string> = {
  'zh-CN': 'zh-CN',
  'zh-TW': 'zh-TW',
  en: 'en-US',
  vi: 'vi-VN',
  ko: 'ko-KR',
};

export function isLocale(v: unknown): v is Locale {
  return typeof v === 'string' && (LOCALES as readonly string[]).includes(v);
}

/** 单个语言标签 → 支持的语言；zh-Hant / zh-TW / zh-HK / zh-MO 归繁体，其余中文归简体。 */
export function matchLocale(tag: string | null | undefined): Locale | null {
  if (!tag) return null;
  const t = tag.trim().toLowerCase().replace(/_/g, '-');
  if (!t || t === '*') return null;
  if (isLocale(tag.trim())) return tag.trim() as Locale;
  const [lang, ...rest] = t.split('-');
  switch (lang) {
    case 'zh':
      return rest.some((p) => p === 'hant' || p === 'tw' || p === 'hk' || p === 'mo') ? 'zh-TW' : 'zh-CN';
    case 'en':
      return 'en';
    case 'vi':
      return 'vi';
    case 'ko':
      return 'ko';
    default:
      return null;
  }
}

/**
 * 按优先级（q 值）取第一个支持的语言；没有任何可识别的语言（空、`*`、只有不支持的语言）时返回 null。
 * 服务端用它区分「客户端明确要求了某种语言」与「需要回退到站点默认 / 用户偏好」。
 */
export function preferredLocale(prefs: string | readonly string[] | null | undefined): Locale | null {
  if (!prefs) return null;
  const list =
    typeof prefs === 'string'
      ? prefs
          .split(',')
          .map((part, i) => {
            const [tag, ...params] = part.trim().split(';');
            const q = params.map((p) => p.trim()).find((p) => p.startsWith('q='));
            const weight = q ? Number(q.slice(2)) : 1;
            return { tag: tag ?? '', q: Number.isFinite(weight) ? weight : 0, i };
          })
          .filter((x) => x.tag && x.q > 0)
          .sort((a, b) => b.q - a.q || a.i - b.i)
          .map((x) => x.tag)
      : prefs;
  for (const tag of list) {
    const m = matchLocale(tag);
    if (m) return m;
  }
  return null;
}

/**
 * 协商语言：接受 Accept-Language 头字符串（带 q 值）或浏览器 navigator.languages 列表，
 * 按优先级取第一个支持的语言，都不支持时用 fallback（国内站 zh-CN、海外站 en）。
 */
export function negotiateLocale(prefs: string | readonly string[] | null | undefined, fallback: Locale): Locale {
  return preferredLocale(prefs) ?? fallback;
}

/** 站点默认语言：国内站简体中文，海外站英文 */
export function siteDefaultLocale(siteId: 'cn' | 'global'): Locale {
  return siteId === 'cn' ? 'zh-CN' : 'en';
}

/** 站点提供的语言：国内站只有简体中文（不做多语言），海外站全部语言 */
const CN_LOCALES: readonly Locale[] = ['zh-CN'];
export function siteLocales(siteId: 'cn' | 'global'): readonly Locale[] {
  return siteId === 'cn' ? CN_LOCALES : LOCALES;
}

/** 在站点提供的语言里取请求的语言，不在其中（或未指定）时用站点默认语言 */
export function siteLocale(siteId: 'cn' | 'global', requested: string | null | undefined): Locale {
  return isLocale(requested) && siteLocales(siteId).includes(requested) ? requested : siteDefaultLocale(siteId);
}

export type MessageParams = Record<string, string | number | null | undefined>;

/**
 * 极简 ICU 子集：`{name}` 插值；`{count, plural, one {# item} other {# items}}` 复数（# 为数值）。
 * 不支持嵌套 select；够用即可，前后端共用。
 */
export function formatMessage(template: string, params: MessageParams | undefined, locale: Locale): string {
  if (!params || !template.includes('{')) return template;
  let out = '';
  let i = 0;
  while (i < template.length) {
    const ch = template[i]!;
    if (ch !== '{') {
      out += ch;
      i++;
      continue;
    }
    // 找到配对的 }
    let depth = 0;
    let j = i;
    for (; j < template.length; j++) {
      if (template[j] === '{') depth++;
      else if (template[j] === '}') {
        depth--;
        if (depth === 0) break;
      }
    }
    if (j >= template.length) {
      out += template.slice(i);
      break;
    }
    const body = template.slice(i + 1, j);
    const comma = body.indexOf(',');
    if (comma < 0) {
      const v = params[body.trim()];
      out += v == null ? '' : String(v);
    } else {
      const name = body.slice(0, comma).trim();
      const rest = body.slice(comma + 1);
      const kindEnd = rest.indexOf(',');
      const kind = rest.slice(0, kindEnd).trim();
      const options = rest.slice(kindEnd + 1);
      const value = params[name];
      if (kind === 'plural') {
        const n = Number(value ?? 0);
        const cases = parseCases(options);
        const exact = cases[`=${n}`];
        const cat = new Intl.PluralRules(LOCALE_TAGS[locale]).select(n);
        const chosen = exact ?? cases[cat] ?? cases.other ?? '';
        out += formatMessage(chosen.replace(/#/g, String(n)), params, locale);
      } else if (kind === 'select') {
        const cases = parseCases(options);
        out += formatMessage(cases[String(value)] ?? cases.other ?? '', params, locale);
      } else {
        out += value == null ? '' : String(value);
      }
    }
    i = j + 1;
  }
  return out;
}

function parseCases(src: string): Record<string, string> {
  const cases: Record<string, string> = {};
  let i = 0;
  while (i < src.length) {
    while (i < src.length && /\s/.test(src[i]!)) i++;
    let key = '';
    while (i < src.length && src[i] !== '{' && !/\s/.test(src[i]!)) key += src[i++];
    while (i < src.length && src[i] !== '{') i++;
    if (i >= src.length) break;
    let depth = 0;
    const start = i + 1;
    for (; i < src.length; i++) {
      if (src[i] === '{') depth++;
      else if (src[i] === '}') {
        depth--;
        if (depth === 0) break;
      }
    }
    cases[key] = src.slice(start, i);
    i++;
  }
  return cases;
}
